'use client';

/**
 * **The check that says "this one" on a compact instruction choice** (VALE-355).
 *
 * The instruction sub-forms' multi-way choices — the instruction pills, the
 * transfer method, the transfer-to beneficiary chips, the beneficiary holding
 * type — are hand-rolled controls with no arm in the attribute treatment (see
 * {@link instructionCardClass}'s header), so their selected state is carried by
 * ink alone. Ink alone turned out not to be enough: a review of the drawer read
 * the whole block as *"selected state is grey? unclear"*, and the measurements in
 * `instruction-chrome.ts` say why.
 *
 * A stronger tint fixes the average case. This glyph fixes the cases a tint
 * cannot:
 *
 * · a **screenshot** or a recording, where a 2%-lightness step is inside the
 *   compression noise — which is precisely how the defect was reported;
 * · a reader who **cannot separate the two greys** at all;
 * · a **dim or colour-shifted display**, where the whole slate ramp flattens.
 *
 * It renders in `compact` only. The form chrome fills its active pill near-black
 * and has never been reported as ambiguous, so adding a mark there would move
 * shipped pixels on 22 forms to fix a problem they do not have.
 *
 * **It is the last child of the control, not the first.** The transfer method and
 * the holding type are a fixed two-column grid, so a mark in front of the label
 * would start the selected card's text ~18px right of its neighbour's and the
 * pair would stop reading as a pair. On the right it appends, and every label in
 * the group keeps its left edge.
 *
 * ## `reserveSpace` — for a group that wraps
 *
 * On a **wrapping** group (the instruction pills, the beneficiary chips) a mark
 * that appears and disappears changes its chip's width, which re-flows every chip
 * after it — and on a two-line wrap it can move a chip to the other line. Reported
 * as *"there's visual bugs, it's moving a lot when we click on it"*.
 *
 * With `reserveSpace` the glyph is always laid out and merely turns invisible when
 * inactive, so the chip's width is the same in both states and nothing moves. The
 * fixed-grid controls do not need it — their width is the grid's, not their
 * content's — and paying ~10px of dead space there would be for nothing.
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export function InstructionChoiceMark({
  compact,
  isActive,
  reserveSpace = false,
  className
}: {
  compact: boolean;
  isActive: boolean;
  /** Keep the glyph's width in both states — for a group whose chips wrap. */
  reserveSpace?: boolean;
  className?: string;
}) {
  if (!compact) return null;
  if (!isActive && !reserveSpace) return null;
  return (
    <Check
      aria-hidden
      strokeWidth={3}
      className={cn(
        'text-foreground size-[0.85em] shrink-0',
        !isActive && 'invisible',
        className
      )}
    />
  );
}
